/**
 * Variant D — PLAYGROUND.
 *
 * Conversion archetype borrowed from SaaS try-before-buy (Notion / Airtable /
 * Figma / v0). The hero IS the product: visitor types their idea, hits "Generar"
 * and watches a fake plan materialize live (sequenced reveals + skeleton bars
 * filling in). No screenshots — just the workspace itself, embedded in the page.
 *
 * Single column, light, snappy. Below the hero: a vertical "narrative scroller"
 * with screenshots of the actual app, then privacy + pricing + final CTA.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Sparkles,
  Check,
  Crown,
  Zap,
  Lock,
  Shield,
  EyeOff,
  Download,
  Wand2,
  Loader2,
  Lightbulb,
  Search,
  Cog,
  Users,
  Wallet,
  Presentation,
  X,
  TrendingUp,
  PieChart,
  Trophy,
  Briefcase,
  Coffee,
  GraduationCap,
  Megaphone,
  Truck,
  Building2,
  UtensilsCrossed,
  ShoppingBag,
  LineChart,
  Store,
  Hotel,
} from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { useLang } from "@/contexts/LanguageContext";
import { LANDING_COPY } from "@/data/landingCopy";

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------
const ACCENT = "#2563EB"; // electric blue
const ACCENT_2 = "#7C3AED"; // violet
const INK = "#0B0B10";
const INK_SOFT = "#5B5D6B";
const SURFACE = "#F5F7FB";
const STROKE = "#E3E7EE";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function Overline({ children, className = "", color = ACCENT }) {
  return (
    <div
      className={`text-[11px] uppercase tracking-[0.28em] ${className}`}
      style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 700, color }}
    >
      {children}
    </div>
  );
}

function PrimaryCta({ to, children, large = false, testId, onClick, type, disabled }) {
  const Tag = to ? Link : "button";
  const base = `group inline-flex items-center gap-2 rounded-xl text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-xl ${
    large ? "px-7 py-3.5 text-base" : "px-5 py-2.5 text-sm"
  } ${disabled ? "opacity-50" : ""}`;
  const style = {
    fontFamily: '"Manrope", system-ui, sans-serif',
    fontWeight: 600,
    background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})`,
  };
  return (
    <Tag
      to={to}
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={base}
      style={style}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.2} />
    </Tag>
  );
}

function GhostCta({ to, children, large = false, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-xl border bg-white text-zinc-900 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        large ? "px-7 py-3.5 text-base" : "px-5 py-2.5 text-sm"
      }`}
      style={{ fontFamily: '"Manrope", system-ui, sans-serif', borderColor: STROKE, fontWeight: 500 }}
    >
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Hero playground — interactive idea → live plan preview
// ---------------------------------------------------------------------------

const PROMPTS_ES = [
  "Plataforma EdTech B2B para profesionales LATAM",
  "Café-coworking en Malasaña para creativos remotos",
  "Marketplace B2B de logística sostenible",
  "App de bienestar mental con coach IA",
];
const PROMPTS_EN = [
  "B2B EdTech platform for LATAM professionals",
  "Coffee-coworking in Malasaña for remote creatives",
  "B2B marketplace for sustainable logistics",
  "Mental wellness app with AI coach",
];

// Industry icons (must match order of c.marquee.industries in landingCopy.js)
const INDUSTRY_ICONS = [
  { Icon: Briefcase, tone: "#2563EB" },        // B2B SaaS
  { Icon: Coffee, tone: "#A16207" },           // Café & Coworking
  { Icon: GraduationCap, tone: "#7C3AED" },    // Online Academy
  { Icon: Megaphone, tone: "#EC4899" },        // Digital Agency
  { Icon: Truck, tone: "#F59E0B" },            // Food Truck
  { Icon: Building2, tone: "#0EA5E9" },        // Real Estate
  { Icon: UtensilsCrossed, tone: "#DC2626" },  // Restaurant
  { Icon: ShoppingBag, tone: "#10B981" },      // E-commerce
  { Icon: LineChart, tone: "#6366F1" },        // Consulting
  { Icon: Sparkles, tone: "#F472B6" },         // Aesthetic Clinic
  { Icon: Store, tone: "#8B5CF6" },            // Marketplace
  { Icon: Hotel, tone: "#0891B2" },            // Hospitality
];

const MODULES = [
  {
    k: "concept",
    icon: Lightbulb,
    label_es: "Concepto & misión",
    label_en: "Concept & mission",
    stat_es: "Visión + equity",
    stat_en: "Vision + equity",
    tone: "#F59E0B",
  },
  {
    k: "market",
    icon: Search,
    label_es: "Mercado · TAM",
    label_en: "Market · TAM",
    stat_es: "€640M / +18%",
    stat_en: "€640M / +18%",
    tone: "#10B981",
  },
  {
    k: "ops",
    icon: Cog,
    label_es: "Operaciones",
    label_en: "Operations",
    stat_es: "Margen 62%",
    stat_en: "62% margin",
    tone: "#2563EB",
  },
  {
    k: "hr",
    icon: Users,
    label_es: "Equipo & RRHH",
    label_en: "Team & HR",
    stat_es: "5 perfiles clave",
    stat_en: "5 key hires",
    tone: "#7C3AED",
  },
  {
    k: "budget",
    icon: Wallet,
    label_es: "Finanzas · 5 años",
    label_en: "Financials · 5 yrs",
    stat_es: "EBITDA €1,1M",
    stat_en: "EBITDA €1.1M",
    tone: "#0EA5E9",
  },
  {
    k: "deck",
    icon: Presentation,
    label_es: "Pitch deck · 12 slides",
    label_en: "Pitch deck · 12 slides",
    stat_es: "Modo inversor",
    stat_en: "Investor mode",
    tone: "#EC4899",
  },
];

function HeroPlayground({ lang }) {
  const prompts = lang === "en" ? PROMPTS_EN : PROMPTS_ES;
  const [idea, setIdea] = useState(prompts[0]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [revealed, setRevealed] = useState(false);

  // Run a fake generation: 6 modules tick on at staggered intervals
  function generate() {
    setRunning(true);
    setDone(0);
    setRevealed(true);
    MODULES.forEach((_, i) => {
      setTimeout(() => setDone(i + 1), 450 + i * 420);
    });
    setTimeout(() => setRunning(false), 450 + MODULES.length * 420);
  }

  // Auto-run on first mount so visitors immediately see the magic
  useEffect(() => {
    const id = setTimeout(generate, 600);
    return () => clearTimeout(id);
  }, []);

  function cycle(direction = 1) {
    const idx = prompts.indexOf(idea);
    const next = (idx + direction + prompts.length) % prompts.length;
    setIdea(prompts[next]);
    setRevealed(false);
    setDone(0);
  }

  const progress = (done / MODULES.length) * 100;

  return (
    <div className="relative mx-auto w-full max-w-3xl">
      {/* Halo */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-10 rounded-[40px] opacity-30 blur-3xl"
        style={{ background: `radial-gradient(ellipse 70% 60% at 50% 30%, ${ACCENT}55, transparent 70%)` }}
      />

      <div className="relative rounded-3xl border bg-white shadow-2xl" style={{ borderColor: STROKE }}>
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: STROKE }}>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#ff5f57" }} />
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#febc2e" }} />
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#28c840" }} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: INK_SOFT }}>
            atelier.business · playground
          </div>
          <div className="text-[10px]" style={{ color: INK_SOFT }}>●</div>
        </div>

        {/* Prompt bar */}
        <div className="border-b p-5 sm:p-6" style={{ borderColor: STROKE }}>
          <Overline color={ACCENT}>{lang === "es" ? "Describe tu idea en 1 frase" : "Describe your idea in 1 line"}</Overline>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div
              className="flex-1 rounded-xl border px-4 py-3"
              style={{ borderColor: STROKE, background: SURFACE }}
            >
              <input
                value={idea}
                onChange={(e) => {
                  setIdea(e.target.value);
                  setRevealed(false);
                  setDone(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") generate();
                }}
                placeholder={lang === "es" ? "Ej. Plataforma EdTech B2B…" : "Ex. B2B EdTech platform…"}
                className="w-full bg-transparent text-base outline-none placeholder:text-zinc-400"
                style={{ color: INK, fontFamily: '"Manrope", system-ui, sans-serif' }}
                data-testid="hero-playground-input"
              />
            </div>
            <button
              type="button"
              onClick={generate}
              disabled={running || !idea.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
              style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
              data-testid="hero-playground-generate"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.4} />
              ) : (
                <Wand2 className="h-4 w-4" strokeWidth={2.2} />
              )}
              {running
                ? lang === "es" ? "Generando" : "Generating"
                : lang === "es" ? "Generar mi plan" : "Generate my plan"}
            </button>
          </div>
          {/* prompt chips */}
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: INK_SOFT }}>
              {lang === "es" ? "O prueba:" : "Or try:"}
            </span>
            {prompts.slice(0, 3).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setIdea(p);
                  setRevealed(false);
                  setDone(0);
                  setTimeout(generate, 100);
                }}
                className="rounded-full border px-3 py-1 text-[11px] transition-colors hover:bg-zinc-50"
                style={{ borderColor: STROKE, color: INK_SOFT }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Live preview area */}
        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <Overline color={ACCENT_2}>{lang === "es" ? "Vista previa en vivo" : "Live preview"}</Overline>
            <div
              className="inline-flex items-center gap-2 text-[11px]"
              style={{ color: INK_SOFT }}
              data-testid="hero-playground-progress"
            >
              <div
                className="relative h-1.5 w-24 overflow-hidden rounded-full"
                style={{ background: SURFACE }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
                />
              </div>
              <span>{done}/{MODULES.length}</span>
            </div>
          </div>

          <ul className="mt-4 space-y-2">
            {MODULES.map((m, i) => {
              const isDone = revealed && i < done;
              const isActive = revealed && i === done && running;
              const Icon = m.icon;
              return (
                <motion.li
                  key={m.k}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors"
                  style={{
                    borderColor: isDone ? `${m.tone}40` : STROKE,
                    background: isDone ? "white" : isActive ? "#EEF2FF" : SURFACE,
                  }}
                >
                  <span
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all"
                    style={{
                      background: isDone ? ACCENT : "white",
                      border: `1px solid ${isDone ? ACCENT : STROKE}`,
                    }}
                  >
                    {isDone ? (
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    ) : isActive ? (
                      <Loader2 className="h-3 w-3 animate-spin" style={{ color: ACCENT }} strokeWidth={2.5} />
                    ) : null}
                  </span>
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-all"
                    style={{
                      background: isDone ? `${m.tone}15` : "white",
                      border: `1px solid ${isDone ? `${m.tone}40` : STROKE}`,
                      color: isDone ? m.tone : "#94A3B8",
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                  </span>
                  <span
                    className={`text-sm transition-colors ${isDone ? "" : "text-zinc-400"}`}
                    style={{ color: isDone ? INK : undefined }}
                  >
                    {lang === "en" ? m.label_en : m.label_es}
                  </span>
                  {isDone && (
                    <span
                      className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                      style={{
                        background: `${m.tone}15`,
                        color: m.tone,
                        border: `1px solid ${m.tone}30`,
                      }}
                    >
                      {lang === "en" ? m.stat_en : m.stat_es}
                    </span>
                  )}
                </motion.li>
              );
            })}
          </ul>
          {done === MODULES.length && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between"
              style={{ borderColor: ACCENT, background: "#EEF2FF" }}
              data-testid="hero-playground-done"
            >
              <div>
                <div className="text-sm font-medium" style={{ color: INK }}>
                  {lang === "es" ? "¡Listo! Tu business plan está completo." : "Done! Your business plan is ready."}
                </div>
                <div className="text-xs" style={{ color: INK_SOFT }}>
                  {lang === "es"
                    ? "Crea una cuenta gratis y edítalo, exporta o comparte."
                    : "Create a free account to edit, export or share."}
                </div>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white"
                style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
                data-testid="hero-playground-claim-cta"
              >
                {lang === "es" ? "Guardar mi plan" : "Save my plan"}
                <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
              </Link>
            </motion.div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between px-2 text-[11px]" style={{ color: INK_SOFT }}>
        <span>{lang === "es" ? "Preview interactivo · solo en esta página" : "Interactive preview · just on this page"}</span>
        <button
          type="button"
          onClick={() => cycle(1)}
          className="inline-flex items-center gap-1 hover:text-zinc-900"
          data-testid="hero-playground-shuffle"
        >
          {lang === "es" ? "Cambiar ejemplo" : "Shuffle"}
          <ArrowRight className="h-3 w-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Savings table — head-to-head cost comparison vs consultants/freelancers
// ---------------------------------------------------------------------------

function SavingsSection({ lang, copy }) {
  if (!copy) return null;
  return (
    <section
      className="relative px-6 pt-20 pb-14 lg:px-10 lg:pt-24 lg:pb-16"
      data-testid="landing-savings-section"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${STROKE}, transparent)` }}
      />
      <div className="mx-auto max-w-5xl">
        <div className="max-w-2xl">
          <Overline color={ACCENT}>{copy.eyebrow}</Overline>
          <h2
            className="mt-3 text-4xl tracking-tight sm:text-5xl lg:text-[3.5rem]"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              color: INK,
              lineHeight: 1.05,
            }}
          >
            {copy.title}
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: INK_SOFT }}>
            {copy.subtitle}
          </p>
        </div>

        <div
          className="mt-12 overflow-hidden rounded-3xl border bg-white shadow-xl"
          style={{ borderColor: STROKE }}
        >
          {/* Header row */}
          <div
            className="grid grid-cols-[1.4fr_0.9fr_0.7fr_1.4fr] items-center gap-4 border-b px-6 py-4 text-[10px] uppercase tracking-[0.18em]"
            style={{ borderColor: STROKE, background: SURFACE, color: INK_SOFT }}
          >
            <div>{lang === "es" ? "Opción" : "Option"}</div>
            <div>{lang === "es" ? "Coste" : "Cost"}</div>
            <div>{lang === "es" ? "Tiempo" : "Time"}</div>
            <div className="hidden md:block">{lang === "es" ? "Qué te llevas" : "What you get"}</div>
          </div>

          {/* Rows */}
          {copy.rows.map((row, i) => (
            <motion.div
              key={row.k}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={`grid grid-cols-[1.4fr_0.9fr_0.7fr_1.4fr] items-center gap-4 px-6 py-5 ${
                i !== copy.rows.length - 1 ? "border-b" : ""
              }`}
              style={{
                borderColor: STROKE,
                background: row.highlight
                  ? `linear-gradient(120deg, ${ACCENT}10, ${ACCENT_2}10)`
                  : "white",
              }}
              data-testid={`landing-savings-row-${row.k}`}
            >
              <div className="flex items-center gap-3">
                {row.highlight ? (
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-md"
                    style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
                  >
                    <Trophy className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                ) : (
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-xl border"
                    style={{ borderColor: STROKE, background: SURFACE, color: "#94A3B8" }}
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                  </span>
                )}
                <div>
                  <div
                    className="text-base"
                    style={{
                      color: INK,
                      fontWeight: row.highlight ? 700 : 500,
                    }}
                  >
                    {row.label}
                  </div>
                  {row.highlight && (
                    <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: ACCENT }}>
                      {lang === "es" ? "Tú con Atelier" : "You with Atelier"}
                    </div>
                  )}
                </div>
              </div>
              <div
                className="text-xl"
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  color: row.highlight ? ACCENT : INK,
                }}
              >
                {row.price}
              </div>
              <div className="text-sm" style={{ color: row.highlight ? ACCENT : INK_SOFT, fontWeight: row.highlight ? 600 : 400 }}>
                {row.time}
              </div>
              <div className="hidden md:block text-sm leading-relaxed" style={{ color: INK_SOFT }}>
                {row.note}
              </div>
            </motion.div>
          ))}
        </div>

        <div
          className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border bg-white p-5 sm:flex-row sm:items-center"
          style={{ borderColor: STROKE }}
        >
          <div className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
              style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
            >
              <TrendingUp className="h-5 w-5" strokeWidth={2} />
            </span>
            <p
              className="text-base leading-snug"
              style={{ color: INK, fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
            >
              {copy.footer}
            </p>
          </div>
          <PrimaryCta to="/register" testId="landing-savings-cta">
            {lang === "es" ? "Empezar gratis" : "Start free"}
          </PrimaryCta>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Narrative scroller — vertical alternating panels with phone-screenshot style mocks
// ---------------------------------------------------------------------------

function ScreenMock({ kind, lang }) {
  if (kind === "deck") {
    return (
      <div className="relative">
        {/* Decorative back slide */}
        <div
          aria-hidden
          className="absolute -right-6 top-6 hidden h-full w-full rotate-3 rounded-2xl border opacity-60 lg:block"
          style={{ borderColor: STROKE, background: SURFACE }}
        />
        {/* Decorative back slide 2 */}
        <div
          aria-hidden
          className="absolute -right-3 top-3 hidden h-full w-full rotate-1 rounded-2xl border bg-white opacity-80 lg:block"
          style={{ borderColor: STROKE }}
        />
        {/* Main slide */}
        <div
          className="relative overflow-hidden rounded-2xl border shadow-2xl"
          style={{
            borderColor: "#1F2937",
            background: `linear-gradient(135deg, #0B0B10 0%, #1A1B2E 50%, #1E3A8A 100%)`,
            color: "white",
          }}
        >
          {/* Slide chrome */}
          <div
            className="flex items-center justify-between border-b px-5 py-3"
            style={{ borderColor: "rgba(255,255,255,0.08)" }}
          >
            <div className="flex items-center gap-2">
              <span
                className="text-[10px] uppercase tracking-[0.22em]"
                style={{ fontFamily: '"Playfair Display", serif', color: "white", fontWeight: 700 }}
              >
                Atelier <span className="italic" style={{ color: "#93C5FD" }}>Business</span>
              </span>
            </div>
            <div className="flex items-center gap-3 text-[10px]" style={{ color: "#94A3B8" }}>
              <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
                {lang === "es" ? "MODO INVERSOR" : "INVESTOR MODE"}
              </span>
              <span>07 / 12</span>
            </div>
          </div>

          {/* Slide body */}
          <div className="relative px-6 py-7 sm:px-8 sm:py-9">
            <div
              aria-hidden
              className="absolute -left-12 top-12 h-32 w-32 rounded-full opacity-25 blur-3xl"
              style={{ background: ACCENT }}
            />
            <div
              aria-hidden
              className="absolute -right-10 -bottom-12 h-40 w-40 rounded-full opacity-25 blur-3xl"
              style={{ background: ACCENT_2 }}
            />

            <div className="relative">
              <div
                className="text-[10px] uppercase tracking-[0.28em]"
                style={{ color: "#93C5FD", fontWeight: 700 }}
              >
                {lang === "es" ? "Por qué ganamos" : "Why we win"}
              </div>
              <div
                className="mt-3 text-[28px] leading-tight sm:text-4xl"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              >
                {lang === "es" ? (
                  <>10× más rápido que <span className="italic" style={{ color: "#93C5FD" }}>un consultor</span>.</>
                ) : (
                  <>10× faster than <span className="italic" style={{ color: "#93C5FD" }}>a consultant</span>.</>
                )}
              </div>

              <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {[
                  {
                    k: "speed",
                    label: lang === "es" ? "1 tarde vs 3 semanas" : "1 afternoon vs 3 weeks",
                    sub: lang === "es" ? "Plan completo" : "Full plan",
                  },
                  {
                    k: "cost",
                    label: lang === "es" ? "0 € vs 8.000 €" : "$0 vs $8,000",
                    sub: lang === "es" ? "Sin agencia" : "No agency",
                  },
                  {
                    k: "edit",
                    label: lang === "es" ? "Edición infinita" : "Endless edits",
                    sub: lang === "es" ? "PDF estático muerto" : "No more static PDFs",
                  },
                  {
                    k: "export",
                    label: lang === "es" ? "PPTX + PDF + JSON" : "PPTX + PDF + JSON",
                    sub: lang === "es" ? "Lo que pida el VC" : "What the VC asks for",
                  },
                ].map((b) => (
                  <div
                    key={b.k}
                    className="flex items-start gap-2.5 rounded-lg border px-3 py-2.5"
                    style={{
                      borderColor: "rgba(147,197,253,0.25)",
                      background: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: "#93C5FD" }} strokeWidth={3} />
                    <div>
                      <div className="text-[13px] font-semibold" style={{ color: "white" }}>
                        {b.label}
                      </div>
                      <div className="text-[11px]" style={{ color: "#94A3B8" }}>
                        {b.sub}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="mt-6 flex items-center justify-between border-t pt-4 text-[10px] uppercase tracking-[0.18em]"
                style={{ borderColor: "rgba(255,255,255,0.08)", color: "#94A3B8" }}
              >
                <span>
                  {lang === "es" ? "Cap Table · Stress test · Escenarios" : "Cap Table · Stress test · Scenarios"}
                </span>
                <span style={{ color: "#93C5FD", fontWeight: 700 }}>
                  {lang === "es" ? "Listo para Series A" : "Series-A ready"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (kind === "financials") {
    const years = [
      { y: "Y1", h: 28, rev: "€180k" },
      { y: "Y2", h: 44, rev: "€620k" },
      { y: "Y3", h: 60, rev: "€1.4M" },
      { y: "Y4", h: 80, rev: "€2.6M" },
      { y: "Y5", h: 100, rev: "€4.2M" },
    ];
    return (
      <div
        className="overflow-hidden rounded-2xl border bg-white p-5 shadow-2xl sm:p-6"
        style={{ borderColor: STROKE }}
      >
        <div className="flex items-center justify-between">
          <Overline color={ACCENT}>{lang === "es" ? "Finanzas · 5 años" : "Financials · 5 yrs"}</Overline>
          <div
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider"
            style={{ borderColor: `${ACCENT}40`, background: `${ACCENT}10`, color: ACCENT }}
          >
            <TrendingUp className="h-3 w-3" strokeWidth={2.4} />
            {lang === "es" ? "+CAGR 87%" : "+CAGR 87%"}
          </div>
        </div>

        {/* Chart */}
        <div className="mt-5 flex items-end gap-2.5 sm:gap-3">
          {years.map((b, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-2">
              <div className="relative w-full">
                <div
                  className="w-full rounded-md transition-all"
                  style={{
                    height: `${b.h * 1.1}px`,
                    background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT_2})`,
                  }}
                />
                {i === years.length - 1 && (
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase text-white shadow"
                    style={{ background: ACCENT }}
                  >
                    {b.rev}
                  </div>
                )}
              </div>
              <div className="text-[10px] font-medium uppercase" style={{ color: INK_SOFT }}>
                {b.y}
              </div>
            </div>
          ))}
        </div>

        {/* KPIs */}
        <div
          className="mt-5 grid grid-cols-3 gap-3 border-t pt-4"
          style={{ borderColor: STROKE }}
        >
          {[
            { label: lang === "es" ? "Ingresos Y5" : "Revenue Y5", value: "€4.2M", up: "+24%" },
            { label: "EBITDA Y5", value: "€1.1M", up: "+18%" },
            { label: lang === "es" ? "Ronda objetivo" : "Target round", value: "€500k", accent: true },
          ].map((k, i) => (
            <div key={i}>
              <div className="text-[9px] uppercase tracking-wider" style={{ color: INK_SOFT }}>
                {k.label}
              </div>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <div
                  className="text-lg sm:text-xl"
                  style={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    color: k.accent ? ACCENT : INK,
                  }}
                >
                  {k.value}
                </div>
                {k.up && (
                  <div
                    className="inline-flex items-center gap-0.5 text-[10px] font-semibold"
                    style={{ color: "#10B981" }}
                  >
                    <TrendingUp className="h-2.5 w-2.5" strokeWidth={3} />
                    {k.up}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Mini cap table teaser */}
        <div
          className="mt-4 flex items-center justify-between rounded-lg border px-3 py-2"
          style={{ borderColor: STROKE, background: SURFACE }}
        >
          <div className="flex items-center gap-2">
            <PieChart className="h-4 w-4" style={{ color: ACCENT_2 }} strokeWidth={2} />
            <span className="text-[11px] font-semibold" style={{ color: INK }}>
              Cap Table
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {[
              { l: lang === "es" ? "Fund." : "Found.", v: "62%", c: ACCENT },
              { l: "ESOP", v: "10%", c: "#10B981" },
              { l: "Pre-seed", v: "18%", c: ACCENT_2 },
              { l: "Seed", v: "10%", c: "#F59E0B" },
            ].map((s, i) => (
              <span
                key={i}
                className="rounded px-1.5 py-0.5 text-[9px] font-semibold"
                style={{ background: `${s.c}15`, color: s.c }}
              >
                {s.l} {s.v}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // canvas
  const blocks = [
    {
      l: lang === "es" ? "Aliados" : "Key Partners",
      t: lang === "es" ? "Universidades · Coaches" : "Universities · Coaches",
      c: "#0EA5E9",
    },
    {
      l: lang === "es" ? "Actividades" : "Key Activities",
      t: lang === "es" ? "Curaduría + IA" : "Curation + AI",
      c: "#7C3AED",
    },
    {
      l: lang === "es" ? "Propuesta de valor" : "Value Prop",
      t: lang === "es" ? "Plan defendible en 1 tarde" : "Defensible plan in 1 afternoon",
      c: ACCENT,
      accent: true,
    },
    {
      l: lang === "es" ? "Relaciones" : "Relationships",
      t: lang === "es" ? "Onboarding 1:1" : "1:1 onboarding",
      c: "#EC4899",
    },
    {
      l: lang === "es" ? "Segmentos" : "Segments",
      t: lang === "es" ? "Fundadores LATAM/EU" : "LATAM/EU founders",
      c: "#10B981",
    },
    {
      l: lang === "es" ? "Recursos" : "Key Resources",
      t: lang === "es" ? "Modelos IA + plantillas" : "AI models + templates",
      c: "#F59E0B",
    },
    {
      l: lang === "es" ? "Canales" : "Channels",
      t: lang === "es" ? "SEO · Aceleradoras" : "SEO · Accelerators",
      c: "#06B6D4",
    },
    {
      l: lang === "es" ? "Costes" : "Cost Structure",
      t: lang === "es" ? "Infraestructura + IA" : "Infra + AI",
      c: "#94A3B8",
    },
    {
      l: lang === "es" ? "Ingresos" : "Revenue",
      t: lang === "es" ? "Starter 49,90 € · Pro 99,90 €" : "Starter $49.90 · Pro $99.90",
      c: "#10B981",
    },
  ];

  return (
    <div className="relative">
      <div
        className="overflow-hidden rounded-2xl border bg-white p-5 shadow-2xl sm:p-6"
        style={{ borderColor: STROKE }}
      >
        <div className="flex items-center justify-between">
          <Overline color={ACCENT}>Business Model Canvas</Overline>
          <span
            className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-wider"
            style={{ borderColor: `${ACCENT}40`, color: ACCENT, background: `${ACCENT}10` }}
          >
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
            {lang === "es" ? "Sincronizado" : "Live-synced"}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          {blocks.map((b, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-lg border p-2.5"
              style={{
                borderColor: b.accent ? b.c : STROKE,
                background: b.accent
                  ? `linear-gradient(135deg, ${b.c}12, ${b.c}05)`
                  : "white",
                boxShadow: b.accent ? `0 4px 18px ${b.c}25` : "none",
              }}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: b.c }}
                />
                <div
                  className="text-[8.5px] uppercase tracking-wider"
                  style={{ color: b.accent ? b.c : INK_SOFT, fontWeight: 700 }}
                >
                  {b.l}
                </div>
              </div>
              <div
                className="mt-1 text-[11px] leading-snug"
                style={{ color: INK, fontWeight: b.accent ? 600 : 400 }}
              >
                {b.t}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Floating "starting point" seal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
        whileInView={{ opacity: 1, scale: 1, rotate: -6 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="absolute -right-3 -bottom-4 flex items-center gap-2 rounded-xl border bg-white px-3 py-2 shadow-xl sm:-right-6 sm:-bottom-6"
        style={{ borderColor: `${ACCENT}40` }}
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded-lg text-white"
          style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
        >
          <Trophy className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
        <div>
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: ACCENT }}>
            {lang === "es" ? "Punto de partida" : "Starting point"}
          </div>
          <div className="text-[12px]" style={{ color: INK, fontWeight: 600 }}>
            {lang === "es" ? "Un negocio que escala" : "A business that scales"}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function NarrativeScroller({ lang }) {
  const PANELS = [
    {
      k: "speed",
      eyebrow: lang === "es" ? "Velocidad" : "Speed",
      title: lang === "es" ? "Pitch deck para inversores en una tarde." : "Investor pitch deck in one afternoon.",
      desc:
        lang === "es"
          ? "La IA convierte tu idea en 12 slides cinematográficas: problema, solución, mercado, tracción, equipo y la ronda. Modo inversor activado por defecto."
          : "AI turns your idea into 12 cinematic slides: problem, solution, market, traction, team and the round. Investor mode on by default.",
      kind: "deck",
    },
    {
      k: "finance",
      eyebrow: lang === "es" ? "Finanzas reales" : "Real financials",
      title: lang === "es" ? "Proyecciones a 5 años que un VC defiende." : "5-year projections a VC will defend.",
      desc:
        lang === "es"
          ? "Cash flow, EBITDA, stress test y Cap Table sincronizados. Cambia el CAC o el precio y todo el modelo se recalcula al instante. Sin Excel. Sin tocar una fórmula."
          : "Cash flow, EBITDA, stress test and Cap Table — all synced. Change CAC or price and the whole model recalculates instantly. No Excel. No formulas.",
      kind: "financials",
    },
    {
      k: "canvas",
      eyebrow: lang === "es" ? "Estrategia" : "Strategy",
      title: lang === "es" ? "El plano de un negocio que escala." : "The blueprint of a business that scales.",
      desc:
        lang === "es"
          ? "Business Model Canvas vivo: 9 bloques editables, sincronizados con tus finanzas y tu pitch. Es el punto de partida del negocio que vas a construir esta tarde."
          : "A living Business Model Canvas: 9 editable blocks, synced with your financials and your pitch. The starting point of the business you'll build tonight.",
      kind: "canvas",
    },
  ];
  return (
    <div className="space-y-24 lg:space-y-32">
      {PANELS.map((p, idx) => {
        const reverse = idx % 2 === 1;
        return (
          <motion.div
            key={p.k}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className={`grid items-center gap-10 lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <div>
              <Overline color={ACCENT}>{p.eyebrow}</Overline>
              <h3
                className="mt-3 text-3xl tracking-tight sm:text-4xl lg:text-5xl"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK, lineHeight: 1.05 }}
              >
                {p.title}
              </h3>
              <p className="mt-4 max-w-md text-base leading-relaxed" style={{ color: INK_SOFT }}>
                {p.desc}
              </p>
            </div>
            <ScreenMock kind={p.kind} lang={lang} />
          </motion.div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN
// ---------------------------------------------------------------------------

export default function LandingPlayground() {
  const { lang, setLang } = useLang();
  const c = LANDING_COPY[lang === "en" ? "en" : "es"];
  usePageMeta(c.metaTitle, c.metaDesc);

  return (
    <div
      className="min-h-screen"
      style={{ background: "white", color: INK, fontFamily: '"Manrope", system-ui, sans-serif' }}
      data-testid="landing-playground-root"
    >
      {/* HEADER */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur"
        style={{ borderColor: STROKE, background: "rgba(255,255,255,0.85)" }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand-link">
            <span
              className="text-lg tracking-tight"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            >
              Atelier <span className="italic font-normal" style={{ color: ACCENT }}>Business</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-7 text-sm md:flex" style={{ color: INK_SOFT }}>
            <Link to="/catalog" className="transition-colors hover:text-zinc-900" data-testid="landing-nav-templates">
              {c.nav.templates}
            </Link>
            <Link to="/pricing" className="transition-colors hover:text-zinc-900" data-testid="landing-nav-pricing">
              {c.nav.pricing}
            </Link>
            <Link to="/login" className="transition-colors hover:text-zinc-900" data-testid="landing-nav-login">
              {c.nav.login}
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="text-xs uppercase tracking-widest transition-colors hover:text-zinc-900"
              style={{ color: INK_SOFT }}
              data-testid="landing-lang-toggle"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            <PrimaryCta to="/register" testId="landing-nav-cta">
              {c.nav.cta}
            </PrimaryCta>
          </div>
        </div>
      </header>

      {/* HERO — SINGLE COLUMN, MAX FOCUS */}
      <section className="relative overflow-hidden px-6 pt-12 pb-16 lg:px-10 lg:pt-20 lg:pb-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(37,99,235,0.15) 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.4) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage: "radial-gradient(ellipse 60% 50% at 50% 20%, black, transparent 80%)",
          }}
        />
        <div className="relative mx-auto max-w-4xl text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div
              className="inline-flex items-center gap-2 rounded-full border bg-white/80 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] shadow-sm"
              style={{ borderColor: STROKE, color: INK_SOFT }}
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="relative inline-flex h-1.5 w-1.5"
                >
                  <span
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                    style={{ background: ACCENT }}
                  />
                  <span
                    className="relative inline-flex h-1.5 w-1.5 rounded-full"
                    style={{ background: ACCENT }}
                  />
                </span>
                {lang === "es" ? "Pruébalo aquí mismo — sin registro" : "Try it right here — no signup"}
              </span>
            </div>
            <h1
              className="mx-auto mt-6 max-w-3xl text-4xl tracking-tight sm:text-5xl lg:text-6xl"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, lineHeight: 1.05 }}
              data-testid="landing-hero-headline"
            >
              <span style={{ color: INK }}>{c.hero.title_a}</span>
              <br />
              <span
                className="italic bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
              >
                {c.hero.title_b}
              </span>
            </h1>
            <p
              className="mx-auto mt-5 max-w-2xl text-base leading-relaxed sm:text-lg"
              style={{ color: INK_SOFT }}
            >
              {c.hero.subtitle}
            </p>
            <div
              className="mx-auto mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[12px] sm:text-sm"
              style={{ color: INK_SOFT }}
            >
              <span
                className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1"
                style={{ borderColor: STROKE }}
              >
                <X className="h-3.5 w-3.5" style={{ color: "#EF4444" }} strokeWidth={2.4} />
                {lang === "es" ? "Sin consultor de 8.000 €" : "No $8,000 consultant"}
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1"
                style={{ borderColor: STROKE }}
              >
                <X className="h-3.5 w-3.5" style={{ color: "#EF4444" }} strokeWidth={2.4} />
                {lang === "es" ? "Sin Excel infinitos" : "No endless Excels"}
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-white shadow-sm"
                style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
              >
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                {lang === "es" ? "IA + 11 módulos + deck" : "AI + 11 modules + deck"}
              </span>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-xs" style={{ color: INK_SOFT }}>
              <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
              {lang === "es" ? "Empieza aquí" : "Start here"}
            </div>
          </motion.div>

          <div className="mt-10">
            <HeroPlayground lang={lang === "en" ? "en" : "es"} />
          </div>

          {/* trust strip */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs" style={{ color: INK_SOFT }}>
            <span className="inline-flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={3} />
              {lang === "es" ? "Sin tarjeta" : "No card"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={2} />
              AES-256
            </span>
            <span className="inline-flex items-center gap-1.5">
              <EyeOff className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={2} />
              {lang === "es" ? "Cero entrenamiento IA con tus datos" : "Zero AI training on your data"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" style={{ color: ACCENT }} strokeWidth={2} />
              {lang === "es" ? "Export PDF · PPTX · JSON" : "Export PDF · PPTX · JSON"}
            </span>
          </div>
        </div>
      </section>

      {/* INDUSTRIES TICKER */}
      <section
        className="relative overflow-hidden border-y py-12 lg:py-14"
        style={{ borderColor: STROKE, background: SURFACE }}
        data-testid="landing-industries-marquee"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <Overline color={ACCENT}>
                {lang === "es" ? "Plantillas listas" : "Ready-made templates"}
              </Overline>
              <h3
                className="mt-2 text-2xl tracking-tight sm:text-3xl"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK, lineHeight: 1.1 }}
              >
                {c.marquee.eyebrow}
              </h3>
            </div>
            <Link
              to="/catalog"
              className="inline-flex items-center gap-1.5 text-sm font-semibold"
              style={{ color: ACCENT }}
              data-testid="landing-marquee-see-all"
            >
              {lang === "es" ? "Ver el catálogo completo" : "Browse the full catalog"}
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
            </Link>
          </div>
        </div>

        {/* Fade masks */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20"
          style={{ background: `linear-gradient(90deg, ${SURFACE}, transparent)` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20"
          style={{ background: `linear-gradient(270deg, ${SURFACE}, transparent)` }}
        />

        <div className="relative mt-7 overflow-hidden">
          <div className="flex w-max gap-5 whitespace-nowrap py-2 [animation:marquee_45s_linear_infinite]">
            {[...c.marquee.industries, ...c.marquee.industries].map((label, i) => {
              const idx = i % c.marquee.industries.length;
              const meta = INDUSTRY_ICONS[idx] || INDUSTRY_ICONS[0];
              const Icon = meta.Icon;
              return (
                <Link
                  key={`${label}-${i}`}
                  to="/catalog"
                  className="group inline-flex items-center gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                  style={{ borderColor: STROKE }}
                  data-testid={`landing-industry-card-${idx}`}
                >
                  <span
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, ${meta.tone}18, ${meta.tone}08)`,
                      border: `1px solid ${meta.tone}30`,
                      color: meta.tone,
                    }}
                  >
                    <Icon className="h-6 w-6" strokeWidth={1.8} />
                  </span>
                  <div className="flex flex-col items-start">
                    <span
                      className="text-base"
                      style={{
                        color: INK,
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 600,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      className="text-[10px] uppercase tracking-[0.16em]"
                      style={{ color: meta.tone, fontWeight: 700 }}
                    >
                      {lang === "es" ? "Plantilla lista" : "Template ready"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        <style>{`@keyframes marquee {0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
      </section>

      {/* SAVINGS vs CONSULTANTS */}
      <SavingsSection lang={lang === "en" ? "en" : "es"} copy={c.savings} />

      {/* NARRATIVE SCROLLER */}
      <section className="px-6 pt-12 pb-24 lg:px-10 lg:pt-16 lg:pb-28">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <div
              className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] shadow-sm"
              style={{ borderColor: `${ACCENT}40`, color: ACCENT }}
            >
              <Trophy className="h-3.5 w-3.5" strokeWidth={2.2} />
              {lang === "es" ? "Punto de partida · Negocio que escala" : "Starting point · A business that scales"}
            </div>
            <Overline color={ACCENT} className="mt-4">{c.bento.eyebrow}</Overline>
            <h2
              className="mt-3 text-4xl tracking-tight sm:text-5xl lg:text-6xl"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK, lineHeight: 1.05 }}
            >
              {c.bento.title}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: INK_SOFT }}>
              {lang === "es"
                ? "No es un PDF muerto. Es el plano vivo de tu negocio: pitch, finanzas, equipo y estrategia, todo coherente, todo exportable, todo listo para defender ante un VC mañana."
                : "Not a dead PDF. The living blueprint of your business: pitch, financials, team and strategy — coherent, exportable, ready to defend in front of a VC tomorrow."}
            </p>
          </div>
          <div className="mt-20">
            <NarrativeScroller lang={lang === "en" ? "en" : "es"} />
          </div>
        </div>
      </section>

      {/* PRIVACY */}
      <section
        className="border-y px-6 py-24 lg:px-10 lg:py-32"
        style={{ borderColor: STROKE, background: SURFACE }}
        data-testid="landing-privacy-section"
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid items-start gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <Overline color={ACCENT}>{c.privacy.eyebrow}</Overline>
              <h2
                className="mt-3 text-4xl tracking-tight sm:text-5xl lg:text-6xl"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK, lineHeight: 1.05 }}
              >
                {c.privacy.title}
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed" style={{ color: INK_SOFT }}>
                {c.privacy.subtitle}
              </p>
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="mt-10 inline-flex items-center gap-3 rounded-2xl border bg-white p-5 shadow-xl"
                style={{ borderColor: STROKE }}
              >
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
                  style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
                >
                  <Shield className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em]" style={{ color: ACCENT }}>
                    AES-256 · TLS 1.3
                  </div>
                  <div className="text-lg font-medium" style={{ color: INK }}>
                    {lang === "es" ? "Cifrado bancario" : "Bank-grade encryption"}
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {c.privacy.points.map((p, idx) => {
                const Icon = { encryption: Lock, no_training: EyeOff, isolation: Shield, export: Download }[p.k] || Lock;
                return (
                  <motion.div
                    key={p.k}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                    className="rounded-2xl border bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md"
                    style={{ borderColor: STROKE }}
                    data-testid={`landing-privacy-point-${p.k}`}
                  >
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                      style={{ background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </div>
                    <h3
                      className="mt-4 text-lg leading-tight tracking-tight"
                      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, color: INK }}
                    >
                      {p.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
                      {p.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-xl">
              <Overline color={ACCENT}>{c.pricing.eyebrow}</Overline>
              <h2
                className="mt-3 text-4xl tracking-tight sm:text-5xl"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK, lineHeight: 1.05 }}
              >
                {c.pricing.title}
              </h2>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-sm font-medium underline"
              style={{ color: ACCENT }}
              data-testid="landing-pricing-see-all"
            >
              {c.pricing.see_all}
              <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {c.pricing.tiers.map((tier) => {
              const Icon = tier.k === "pro" ? Crown : tier.k === "starter" ? Zap : Sparkles;
              const accent = tier.k === "starter";
              return (
                <div
                  key={tier.k}
                  className={`relative flex flex-col rounded-2xl border p-7 transition-all hover:-translate-y-1 ${
                    accent ? "shadow-2xl" : ""
                  }`}
                  style={{
                    borderColor: accent ? "transparent" : STROKE,
                    background: accent ? `linear-gradient(140deg, ${ACCENT}, ${ACCENT_2})` : "white",
                    color: accent ? "white" : INK,
                  }}
                  data-testid={`landing-pricing-card-${tier.k}`}
                >
                  {accent && (
                    <span
                      className="absolute -top-3 right-7 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.16em]"
                      style={{ background: INK, color: "white" }}
                    >
                      {c.pricing.most_popular}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                    <Overline color={accent ? "white" : ACCENT}>{tier.name}</Overline>
                  </div>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span
                      className="text-5xl tracking-tight"
                      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
                    >
                      {tier.price}
                    </span>
                    <span className={accent ? "text-sm opacity-90" : "text-sm"} style={{ color: accent ? "white" : INK_SOFT }}>
                      {tier.per}
                    </span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.4} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <Link
                      to={tier.to}
                      data-testid={`landing-pricing-${tier.k}-cta`}
                      className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all"
                      style={{
                        background: accent ? "white" : INK,
                        color: accent ? ACCENT : "white",
                      }}
                    >
                      {tier.cta}
                      <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="relative overflow-hidden px-6 py-28 lg:px-10 lg:py-36"
        data-testid="landing-final-cta"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${ACCENT}15 0%, transparent 70%), ${SURFACE}`,
        }}
      >
        <div className="relative mx-auto max-w-3xl text-center">
          <Overline color={ACCENT}>{c.final.eyebrow}</Overline>
          <h2
            className="mt-4 text-5xl tracking-tight sm:text-6xl lg:text-7xl"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK, lineHeight: 1.02 }}
          >
            <span>{c.final.title_a}</span>{" "}
            <span
              className="italic bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(120deg, ${ACCENT}, ${ACCENT_2})` }}
            >
              {c.final.title_b}
            </span>
          </h2>
          <div className="mt-9 flex flex-col items-center gap-3">
            <PrimaryCta to="/register" testId="landing-final-cta-btn" large>
              {c.final.cta}
            </PrimaryCta>
            <p className="text-xs uppercase tracking-[0.2em]" style={{ color: INK_SOFT }}>
              {c.final.sub}
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t bg-white px-6 py-10 lg:px-10" style={{ borderColor: STROKE }}>
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="text-sm" style={{ color: INK_SOFT }}>
            <span style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: INK }}>
              Atelier <span className="italic" style={{ color: ACCENT }}>Business</span>
            </span>
            <span className="mx-2">·</span>
            {c.footer_rights}
          </div>
          <div className="flex gap-6 text-sm" style={{ color: INK_SOFT }}>
            <Link to="/catalog" className="hover:text-zinc-900" data-testid="landing-footer-catalog">{c.nav.templates}</Link>
            <Link to="/pricing" className="hover:text-zinc-900" data-testid="landing-footer-pricing">{c.nav.pricing}</Link>
            <Link to="/login" className="hover:text-zinc-900" data-testid="landing-footer-login">{c.nav.login}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
